import Swal, { type SweetAlertResult } from 'sweetalert2'

import type { IErrorResponse } from '@/types/AoeTypes.ts'

import type { ComposerTranslation } from 'vue-i18n'

import { useStoreLogger } from '@/logger.ts'

import axios from 'axios'

import type { AxiosError } from 'axios'

import type { IAoeCart } from '@/types/CartTypes.ts'

import { GetLogoutUrl } from '@/stores/SettingsStore.ts'

import { ErrorCategory, type IAppError } from '@/types/ErrorTypes.ts'

import {
  getBraintreeErrorLabelKey,
  PAYMENT_CREATION_UNKNOWN_ERROR_KEY,
} from '@/constants/BraintreeErrorCodes.ts'



export function categorizeError(error: unknown): ErrorCategory {

  if (!axios.isAxiosError(error)) return ErrorCategory.Unknown



  const axiosError = error as AxiosError

  if (!axiosError.response) return ErrorCategory.Network



  const status = axiosError.response.status

  if (status === 401) return ErrorCategory.Auth

  if (status === 403) return ErrorCategory.Permission

  if (status >= 500) return ErrorCategory.Server



  const data = axiosError.response.data as IErrorResponse | undefined

  if (data?.responseStatus?.errorCode) return ErrorCategory.Business



  return ErrorCategory.Unknown

}



export function toAppError(error: unknown): IAppError {

  const category = categorizeError(error)



  if (axios.isAxiosError(error)) {

    const axiosError = error as AxiosError

    const data = axiosError.response?.data as IErrorResponse | undefined

    return {

      category,

      code: data?.responseStatus?.errorCode ?? axiosError.code,

      message: data?.responseStatus?.message ?? axiosError.message,

      fieldName: data?.responseStatus?.errors?.[0]?.fieldName,

      originalError: error,

    }

  }



  if (error instanceof Error) {

    return { category, message: error.message, originalError: error }

  }



  return { category, message: String(error), originalError: error }

}



export interface SwalOverrides {

  title?: string

  confirmButtonText?: string

}



export async function handleError(

  error: unknown,

  t: ComposerTranslation,

  overrides?: SwalOverrides,

): Promise<void> {

  await AlertError(t, error, overrides)

}



export async function AlertError(

  t: ComposerTranslation,

  error: unknown,

  overrides?: SwalOverrides,

): Promise<SweetAlertResult<unknown> | null> {

  const logger = useStoreLogger()

  logger.debug('AlertErrorResponse error:', error)



  if (axios.isAxiosError(error)) {

    const axiosError = error as AxiosError

    if (axiosError.response?.status === 401) {

      const logoutUrl = await GetLogoutUrl()

      // empty/self logout URL would reload this page and loop on every request
      if (logoutUrl && logoutUrl !== window.location.href) {
        window.location.href = logoutUrl
      }

      return null

    }

    if (axiosError.response?.status === 403) {

      return Swal.fire({

        text: t('alerts.NoPermissions', { 0: axiosError.config?.url ?? '' }),

        icon: 'error',

      })

    }

    if (axiosError.response && axiosError.response.data) {

      logger.error(axiosError.response.data)

      const errorResponse = axiosError.response.data as IErrorResponse

      return AlertErrorResponse(t, errorResponse)

    }

    return Swal.fire({

      text: t('alerts.AxiosError', {

        message: axiosError.message,

        name: axiosError.name,

        code: axiosError.code,

      }),

      icon: 'error',

    })

  }



  if (error instanceof Error && error.message) {

    return Swal.fire({

      title: overrides?.title,

      text: error.message,

      icon: 'error',

      confirmButtonText: overrides?.confirmButtonText,

    })

  }



  AlertUnknownError(t)

  return null

}



function lowercaseFirstLetter(value: string) {

  return value.charAt(0).toLowerCase() + value.slice(1);

}



export async function AlertErrorResponse(

  t: ComposerTranslation,

  errorResponse: IErrorResponse,

): Promise<SweetAlertResult<any> | null> {

  const logger = useStoreLogger();

  if (

    errorResponse &&

    errorResponse.responseStatus &&

    errorResponse.responseStatus.errorCode

  ) {

    let messageKey = "";

    let fieldName = "";

    if (errorResponse && errorResponse.responseStatus) {

      messageKey = errorResponse.responseStatus.errorCode;

      if (

        errorResponse.responseStatus.meta &&

        errorResponse.responseStatus.meta.errorSubCode

      ) {

        messageKey += `.${errorResponse.responseStatus.meta.errorSubCode}`;

      }

      if (

        errorResponse.responseStatus.errors &&

        errorResponse.responseStatus.errors.length > 0

      ) {

        fieldName = errorResponse.responseStatus.errors[0].fieldName;

      }

    }

    if (messageKey.length === 0) {

      messageKey = "UnknownError";

    }

    messageKey = `alerts.${messageKey}`;

    if (fieldName.length > 0) {

      let possibleColumnName = `columns.${fieldName}`;

      let translatedColumnName = t(possibleColumnName);

      if (possibleColumnName == translatedColumnName) {

        possibleColumnName = `columns.${lowercaseFirstLetter(fieldName)}`;

        translatedColumnName = t(possibleColumnName);

      }

      if (possibleColumnName != translatedColumnName) {

        fieldName = translatedColumnName;

      }

    }

    

    // Extract parameters from error message for shipping restrictions

    const translationParams: Record<string, string> = { fieldName };

    const errorMessage = errorResponse.responseStatus.message || '';

    

    // Extract SKU code from message like "SKU '100461' is restricted..."

    const skuMatch = errorMessage.match(/SKU '([^']+)'/);

    if (skuMatch) {

      translationParams.skuCode = skuMatch[1];

    }

    

    // Extract country code from message like "Country 'US' is restricted..."

    const countryMatch = errorMessage.match(/Country '([^']+)'/);

    if (countryMatch) {

      translationParams.countryCode = countryMatch[1];

    }

    

    let messageText = t(messageKey, translationParams);

    logger.debug(

      "AlertErrorResponse messageKey:",

      messageKey,

      "messageText:",

      messageText,

      "params:",

      translationParams,

    );

    if (messageKey === messageText) {

      messageText = t("alerts.GeneralError", { messageKey });

    }

    return Swal.fire({

      text: messageText,

      icon: "error",

      didOpen: (popup) => {

        popup.dataset.messageid = messageKey;

      },

    });

  }

  return null;

}





export function AlertUnknownError(t: ComposerTranslation): void {

  Swal.fire({

    text: t('alerts.UnknownError'),

    icon: 'error',

    didOpen: (popup) => {

      popup.dataset.messageid = 'alerts.UnknownError'

    },

  })

}



function getPaymentCreationErrorMessageKey(messageData: unknown): string {

  // no response body: request timed out or a proxy returned a non-JSON error page
  if (messageData === null || messageData === undefined) {

    return 'alerts.PaymentCreation.PaymentServiceUnavailable'

  }

  if (typeof messageData !== 'object') {

    return PAYMENT_CREATION_UNKNOWN_ERROR_KEY

  }

  // threeDSecureInfo posted when the bank did not authenticate the card
  if ('liabilityShiftPossible' in messageData) {

    return 'alerts.PaymentCreation.ThreeDSecureFailed'

  }

  // BraintreeError from hosted fields tokenize or 3-D Secure verifyCard
  const { code, type } = messageData as { code?: unknown, type?: unknown }

  if (typeof code === 'string' || typeof type === 'string') {

    return getBraintreeErrorLabelKey(
      typeof code === 'string' ? code : '',
      typeof type === 'string' ? type : '',
    )

  }

  return PAYMENT_CREATION_UNKNOWN_ERROR_KEY

}



/**
 * Alerts for an `aoepaymentcreationerror` message. Server errors with a
 * responseStatus use the error-code lookup; Braintree SDK, 3-D Secure and
 * empty-response failures get specific messages; anything else is unknown.
 */
export async function AlertPaymentCreationError(
  t: ComposerTranslation,
  messageData: unknown,
): Promise<void> {

  const logger = useStoreLogger()

  logger.error('AlertPaymentCreationError messageData:', messageData)



  const result = await AlertErrorResponse(t, messageData as IErrorResponse)

  if (result) {

    return

  }



  const messageKey = getPaymentCreationErrorMessageKey(messageData)

  await Swal.fire({

    text: t(messageKey),

    icon: 'error',

    didOpen: (popup) => {

      popup.dataset.messageid = messageKey

    },

  })

}



export function AlertCartSubmitResponse(

  t: ComposerTranslation,

  cart: IAoeCart,

): void {

  const logger = useStoreLogger()

  logger.debug('AlertCartSubmitResponse response:', cart)

  let messageText = ''



  if (cart.submitErrors) {

    cart.submitErrors.forEach(submitError => {

      const messageKey = `alerts.CartSubmit.${submitError.errorCode}`

      if (submitError.attributes) {

        messageText += `${t(messageKey, submitError.attributes)}<br>`

      } else {

        messageText += `${t(messageKey)}<br>`

      }

    })

  }



  if (messageText.length === 0) {

    const messageKey = `alerts.CartSubmit.UnknownError`

    messageText = t(messageKey)

  }



  Swal.fire({

    html: messageText,

    icon: 'error',

  })

}



export function AlertMasterOrderSubmitResponse(

  t: ComposerTranslation,

  carts: IAoeCart[],

): void {

  const logger = useStoreLogger()

  logger.debug('AlertCartSubmitResponse response:', carts)

  let messageText = ''



  for (const cart of carts) {

    if (cart.submitErrors) {

      cart.submitErrors.forEach(submitError => {

        const messageKey = `alerts.CartSubmit.${submitError.errorCode}`

        messageText += cart.customerFirstName + ' ' + cart.customerLastName + ' - '

        if (submitError.attributes) {

          messageText += `${t(messageKey, submitError.attributes)}<br>`

        } else {

          messageText += `${t(messageKey)}<br>`

        }

      })

    }

  }



  if (messageText.length === 0) {

    const messageKey = `alerts.CartSubmit.UnknownError`

    messageText = t(messageKey)

  }



  Swal.fire({

    html: messageText,

    icon: 'error',

  })

}

